import { useEffect, useRef, useState } from "react";
import {
    Alert,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Stack,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { v4 as uuidv4 } from "uuid";
import {
    fetchWorkerSaleslistPushStatus,
    pushWorkerToSaleslist,
} from "../../api/workersApi";
import { extractErrorMessageSync } from "../../utils/errorHandler";
import {
    COMMAND_STATUS_POLL_INITIAL_DELAY_MS,
    COMMAND_STATUS_POLL_MAX_TRANSIENT_FAILURES,
    commandStatusPollDelay,
    commandWasDefinitivelyRejected,
} from "./feedbackCommandKey";

interface PendingCommand {
    workerId: string;
    fingerprint: string;
    commandId: string;
}

export function WorkerSaleslistPushDialog({
    open,
    workerId,
    workerName,
    onClose,
    onQueued,
}: {
    open: boolean;
    workerId: string;
    workerName: string;
    onClose: () => void;
    onQueued: (message: string) => void;
}) {
    const [createOpportunity, setCreateOpportunity] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [trackedCommand, setTrackedCommand] = useState<PendingCommand | null>(null);
    const pending = useRef<PendingCommand | null>(null);
    const interactionGeneration = useRef(0);
    const activeWorkerId = useRef(workerId);
    activeWorkerId.current = workerId;

    useEffect(() => {
        interactionGeneration.current += 1;
        setSubmitting(false);
        setError(null);
    }, [open, workerId]);

    useEffect(() => {
        pending.current = null;
        setTrackedCommand(null);
    }, [workerId]);

    useEffect(() => {
        if (!trackedCommand) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let transientFailures = 0;
        const schedule = (delay: number) => {
            if (!cancelled) timer = setTimeout(() => void poll(), delay);
        };
        const poll = async () => {
            try {
                const result = await fetchWorkerSaleslistPushStatus(
                    trackedCommand.workerId,
                    trackedCommand.commandId,
                );
                if (cancelled || activeWorkerId.current !== trackedCommand.workerId) return;
                if (result.state === "SENT") {
                    clearPendingCommand(pending, trackedCommand);
                    setTrackedCommand(null);
                    setNotice(null);
                    onQueued("Worker is available in Saleslist");
                    onClose();
                    return;
                }
                if (result.state === "FAILED") {
                    if (result.retryable === false) {
                        clearPendingCommand(pending, trackedCommand);
                    }
                    setTrackedCommand(null);
                    setNotice(null);
                    setError(
                        result.retryable === false
                            ? "Saleslist rejected this publication. Review the worker data before trying again."
                            : "Saleslist delivery could not be confirmed. Retry to reconcile the same command.",
                    );
                    return;
                }
                transientFailures = 0;
            } catch (pollError) {
                if (cancelled) return;
                if (commandWasDefinitivelyRejected(pollError)) {
                    setTrackedCommand(null);
                    setNotice(null);
                    setError("Saleslist command status is no longer accessible. Refresh your permissions or retry the same command.");
                    return;
                }
                transientFailures += 1;
                if (transientFailures >= COMMAND_STATUS_POLL_MAX_TRANSIENT_FAILURES) {
                    setTrackedCommand(null);
                    setNotice(null);
                    setError("Saleslist command status is temporarily unavailable. Retry to reconcile the same command.");
                    return;
                }
            }
            schedule(commandStatusPollDelay(transientFailures));
        };
        schedule(COMMAND_STATUS_POLL_INITIAL_DELAY_MS);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [onClose, onQueued, trackedCommand]);

    const submit = async () => {
        const targetWorkerId = workerId;
        const fingerprint = JSON.stringify({ createOpportunity });
        if (
            pending.current?.workerId !== targetWorkerId ||
            pending.current.fingerprint !== fingerprint
        ) {
            pending.current = {
                workerId: targetWorkerId,
                fingerprint,
                commandId: uuidv4(),
            };
        }
        const command = pending.current;
        const generation = interactionGeneration.current;
        setSubmitting(true);
        setError(null);
        setNotice(null);
        try {
            const result = await pushWorkerToSaleslist(
                targetWorkerId,
                { createOpportunity },
                command.commandId,
            );
            if (activeWorkerId.current !== targetWorkerId || interactionGeneration.current !== generation) return;
            const acknowledgedCommand = result.data.commandId === command.commandId
                ? command
                : { ...command, commandId: result.data.commandId };
            pending.current = acknowledgedCommand;
            if (result.data.state === "FAILED") {
                if (result.data.retryable === false) clearPendingCommand(pending, acknowledgedCommand);
                setError(
                    result.data.retryable === false
                        ? "Saleslist rejected this publication. Review the worker data before trying again."
                        : "Saleslist delivery could not be confirmed. Retry to reconcile the same command.",
                );
                return;
            }
            if (result.data.state === "SENT") {
                clearPendingCommand(pending, acknowledgedCommand);
                setTrackedCommand(null);
                onQueued("Worker is available in Saleslist");
                onClose();
            } else {
                setTrackedCommand(acknowledgedCommand);
                setNotice("Saleslist push queued for delivery.");
            }
        } catch (submitError) {
            if (commandWasDefinitivelyRejected(submitError)) clearPendingCommand(pending, command);
            if (activeWorkerId.current === targetWorkerId && interactionGeneration.current === generation) {
                setError(extractErrorMessageSync(submitError));
            }
        } finally {
            if (activeWorkerId.current === targetWorkerId && interactionGeneration.current === generation) setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !submitting && onClose()}>
            <DialogTitle>Push to Saleslist</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <DialogContentText>
                        Queue {workerName} for durable publication to Saleslist.
                    </DialogContentText>
                    {error && <Alert severity="error">{error}</Alert>}
                    {notice && <Alert severity="info">{notice}</Alert>}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={createOpportunity}
                                onChange={(event) => setCreateOpportunity(event.target.checked)}
                                disabled={submitting || Boolean(trackedCommand)}
                            />
                        }
                        label="Create sales opportunity"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Close</Button>
                <Button
                    variant="contained"
                    onClick={() => void submit()}
                    disabled={submitting || Boolean(trackedCommand)}
                    startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
                >
                    {submitting ? "Queueing..." : "Queue"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function clearPendingCommand(
    pending: { current: PendingCommand | null },
    completed: PendingCommand,
): void {
    if (
        pending.current?.workerId === completed.workerId &&
        pending.current.fingerprint === completed.fingerprint &&
        pending.current.commandId === completed.commandId
    ) {
        pending.current = null;
    }
}
